import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, AlertModal } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { getRoles, deleteRole, getModulesWithPermissions } from '@/services/roleService';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useTranslation } from '@/hooks/useTranslation';
import { buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import type { RoleSummaryDto, RoleType } from '@/types/role';
import type { FilterFieldConfig } from '@/types/filters';
import type { TableSearchParams } from '@utils/tabHelpers';
import ViewRoleModal from './ViewRoleModal';
import styles from './RoleListPage.module.scss';

// ============================================
// TYPES
// ============================================

interface RoleListPageProps {
  routeTo: string;
  /** Route path for the create/edit full-page stepper (e.g. '/w/roles/add') */
  addRoute: string;
  searchParams: TableSearchParams;
}

// ============================================
// HELPERS
// ============================================

function RoleTypeBadge({ roleType }: { roleType: RoleType }) {
  const badgeClass = roleType === 'SYSTEM'
    ? styles.badgeNeutral
    : roleType === 'SUPER_ADMIN'
      ? styles.badgeBrand
      : styles.badgeSuccess;
  return (
    <span className={`${styles.badge} ${badgeClass}`}>
      {toTitleCase(roleType)}
    </span>
  );
}


// ============================================
// COMPONENT
// ============================================

function RoleListPage({ routeTo, addRoute, searchParams }: RoleListPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = searchParams;
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // ── Filters ──
  // Filter fields ordered to match columns: Name, Description, Type, Created
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'name',
        label: t('K_COLUMN_ROLE_NAME', 'Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_NAME', 'Search name...'),
      },
      {
        fieldKey: 'roleType',
        label: t('K_FILTER_ROLE_TYPE', 'Role Type'),
        type: 'select',
        options: [
          { label: t('K_TAB_SYSTEM', 'System'), value: 'SYSTEM' },
          { label: t('K_TAB_CUSTOM', 'Custom'), value: 'CUSTOM' },
          { label: t('K_TAB_SUPER_ADMIN', 'Super Admin'), value: 'SUPER_ADMIN' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo,
    filtersParam,
    fields: filterFields,
  });

  // ── Navigation to create/edit/clone ──
  const handleCreate = useCallback(() => {
    void navigate({ to: addRoute });
  }, [navigate, addRoute]);

  const handleEdit = useCallback(
    (roleId: string) => {
      void navigate({ to: addRoute, search: { id: roleId } });
    },
    [navigate, addRoute],
  );

  const handleClone = useCallback(
    (roleId: string) => {
      void navigate({ to: addRoute, search: { cloneId: roleId } });
    },
    [navigate, addRoute],
  );

  // ── Modals (view + delete only) ──
  const [viewItem, setViewItem] = useState<RoleSummaryDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<RoleSummaryDto | null>(null);
  const [auditRole, setAuditRole] = useState<{ id: string; name: string } | null>(null);

  const handleCloseView = useCallback(() => setViewItem(null), []);

  const handleEditFromView = useCallback(() => {
    if (viewItem) {
      handleEdit(viewItem.id);
      setViewItem(null);
    }
  }, [viewItem, handleEdit]);

  const handleDeleteFromView = useCallback(() => {
    if (viewItem) {
      setDeleteItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleAuditFromView = useCallback(() => {
    if (viewItem) {
      setAuditRole({ id: viewItem.id, name: viewItem.name });
      setViewItem(null);
    }
  }, [viewItem]);

  // Lazy-fetch permission catalogue when the audit modal is open so the
  // modal can resolve permission UUIDs in role audit events to names.
  // Cached for 5 min — repeated modal opens during a session reuse it.
  const { data: modules } = useQuery({
    queryKey: ['modules-with-permissions'],
    queryFn: getModulesWithPermissions,
    enabled: auditRole !== null,
  });

  const resolveItemLabel = useMemo(() => {
    if (!modules) return undefined;
    const map = new Map<string, string>();
    for (const mod of modules) {
      for (const p of mod.permissions) {
        map.set(p.id, p.name);
      }
    }
    return (key: string) => map.get(key) ?? null;
  }, [modules]);

  const handleCloseDelete = useCallback(() => setDeleteItem(null), []);

  // ── Delete mutation ──
  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      setDeleteItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (deleteItem) {
      deleteMutation.mutate(deleteItem.id);
    }
  }, [deleteItem, deleteMutation]);

  // ── Columns ──
  const columns = useMemo<DataTableColumn<RoleSummaryDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_ROLE_NAME', 'Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name}>{item.name}</span>
        ),
      },
      {
        id: 'description',
        header: t('K_COLUMN_DESCRIPTION', 'Description'),
        cell: (item) => (
          <span className={styles.cellSecondary} title={item.description || '-'}>{item.description || '-'}</span>
        ),
      },
      {
        id: 'roleType',
        header: t('K_COLUMN_ROLE_TYPE', 'Type'),
        cell: (item) => <RoleTypeBadge roleType={item.roleType} />,
      },
      {
        id: 'createdAt',
        header: t('K_COLUMN_CREATED_AT', 'Created'),
        sortable: true,
        sortKey: 'createdAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.createdAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [t],
  );

  const rowActions = useCallback((item: RoleSummaryDto) => {
    const isSystem = item.roleType === 'SYSTEM';
    const isSuperAdmin = item.roleType === 'SUPER_ADMIN';
    const actions: ProfileAction[] = [];
    if (hasPermission('ROLES_CREATE')) {
      actions.push({ id: 'clone', label: t('K_CLONE', 'Clone'), onClick: () => handleClone(item.id) });
    }
    if (!isSystem && !isSuperAdmin && hasPermission('ROLES_UPDATE')) {
      actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => handleEdit(item.id) });
    }
    if (!isSystem && !isSuperAdmin && hasPermission('ROLES_DELETE')) {
      actions.push({ id: 'delete', label: t('K_DELETE', 'Delete'), onClick: () => setDeleteItem(item) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => setAuditRole({ id: item.id, name: item.name }),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, handleEdit, handleClone, hasPermission]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  // ── Data fetching ──
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['roles', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getRoles({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);
  const getRowKey = useCallback((item: RoleSummaryDto) => item.id, []);
  const handleRowClick = useCallback((item: RoleSummaryDto) => setViewItem(item), []);

  // ── Navigation ──
  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  // ── Empty state ──
  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_ROLES', 'No roles yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_ROLE', 'Get started by creating your first role.')}
          </p>
          {hasPermission('ROLES_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_ROLE', 'Create Role')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_ROLES', 'Roles')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_ROLES', 'Manage roles and permissions')}</p>
        </div>
        {hasPermission('ROLES_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_ROLE', 'Create Role')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Roles list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      {/* View Modal */}
      <ViewRoleModal
        isOpen={viewItem !== null}
        onClose={handleCloseView}
        roleId={viewItem?.id ?? null}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
        onAudit={handleAuditFromView}
      />

      {/* Delete Confirmation */}
      <AlertModal
        variant="danger"
        title={t('K_MODAL_DELETE_ROLE', 'Delete Role')}
        description={
          t('K_CONFIRM_DELETE_ROLE', 'Are you sure you want to delete the role "{name}"? This action cannot be undone.')
            .replace('{name}', deleteItem?.name ?? '')
        }
        isOpen={deleteItem !== null}
        onClose={handleCloseDelete}
        primaryLabel={t('K_DELETE', 'Delete')}
        onPrimaryClick={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />

      {/* Audit Trail Modal */}
      <AuditTrailModal
        open={auditRole !== null}
        onClose={() => setAuditRole(null)}
        filter={{ resourceType: AUDIT_RESOURCE.ROLE, resourceId: auditRole?.id ?? '' }}
        resolveItemLabel={resolveItemLabel}
        subject={auditRole?.name}
      />
    </div>
  );
}

export default RoleListPage;
